import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

/* -----------------------------
  Helpers
----------------------------- */
function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function uid() {
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

function Icon({ name, className = "h-5 w-5" }) {
  switch (name) {
    case "check":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M20 7 10.5 16.5 4 10"
            stroke="currentColor"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "calendar":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M7 2v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V2h-2v2H9V2H7Zm14 8H3v10h18V10Z"
            fill="currentColor"
          />
        </svg>
      );
    case "download":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3v10m0 0 4-4m-4 4-4-4"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 17v3h16v-3"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      );
    case "pin":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 22s7-5.2 7-12a7 7 0 1 0-14 0c0 6.8 7 12 7 12Zm0-9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
            fill="currentColor"
          />
        </svg>
      );
    case "clock":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Zm1-10V7h-2v6l4 2 1-1.7-3-1.3Z"
            fill="currentColor"
          />
        </svg>
      );
    case "shield":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3Zm-1 14-3-3 1.4-1.4L11 13.2l3.6-3.6L16 11l-5 5Z"
            fill="currentColor"
          />
        </svg>
      );
    case "arrowRight":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M10 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
}

/* -----------------------------
  MOCK "revealed" doctor data
  (only shown after payment)
----------------------------- */
const REVEALED_DOCTORS = [
  {
    id: "d1",
    name: "Dr. Emily Carter",
    specialty: "Physiotherapy",
    clinic: "OrbisCare Physio & Rehab",
    address: "Suite 204, 123 King St W, Toronto, ON M5H 1J9",
    mapHint: "Near St. Andrew Station",
    price: 95,
    rating: 4.7,
  },
  {
    id: "d2",
    name: "Dr. Liam Thompson",
    specialty: "Dentistry",
    clinic: "BrightSmile Dental Studio",
    address: "210–2180 W Broadway, Vancouver, BC V6K 2E1",
    mapHint: "Across from Kitsilano Community Centre",
    price: 120,
    rating: 4.5,
  },
  {
    id: "d3",
    name: "Dr. Ava Singh",
    specialty: "Cardiology",
    clinic: "HeartFirst Cardiology Clinic",
    address: "Suite 510, 906 8 Ave SW, Calgary, AB T2P 1H9",
    mapHint: "Near Calgary Tower",
    price: 210,
    rating: 4.8,
  },
  {
    id: "d4",
    name: "Dr. Noah Patel",
    specialty: "ENT",
    clinic: "ClearEar ENT Specialists",
    address: "3rd Floor, 200 Elgin St, Ottawa, ON K2P 1L5",
    mapHint: "Near Confederation Park",
    price: 130,
    rating: 4.4,
  },
  {
    id: "d5",
    name: "Dr. Olivia Martin",
    specialty: "Orthopedics",
    clinic: "MoveWell Orthopedics & Sports Medicine",
    address: "Unit 12, 8888 University Dr NW, Edmonton, AB T6G 2X8",
    mapHint: "Near University of Alberta Hospital",
    price: 175,
    rating: 4.6,
  },
];


/* -----------------------------
  ICS calendar download (no libs)
----------------------------- */
function downloadICS({ title, description, location, startISO, endISO }) {
  // ICS needs UTC in format YYYYMMDDTHHMMSSZ
  const toICS = (iso) => iso.replace(/[-:]/g, "").replace(".000", "") + "Z";
  const now = new Date().toISOString();

  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//OrbisMed//Appointment//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid()}@orbismed`,
    `DTSTAMP:${toICS(now)}`,
    `DTSTART:${toICS(startISO)}`,
    `DTEND:${toICS(endISO)}`,
    `SUMMARY:${escapeICS(title)}`,
    `DESCRIPTION:${escapeICS(description)}`,
    `LOCATION:${escapeICS(location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "orbismed-appointment.ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function escapeICS(str) {
  return String(str || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

/* -----------------------------
  Page
----------------------------- */
export default function Confirmation() {
  const q = useQuery(); // keep (not harmful)

  // 1️ Read booking from localStorage (HOOK 1)
  const booking = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("pending_booking"));
    } catch {
      return null;
    }
  }, []);

  // 2️ Booking ID (HOOK 2)
  const bookingId = useMemo(
    () =>
      `ORB-${Math.random().toString(10).slice(2, 8)}-${new Date().getFullYear()}`,
    []
  );

  // 3️ Safe derived values (NO hooks here)
  const doctorId = booking?.doctorId;
  const slot = booking?.slot;
  const mode = booking?.mode;
  const patientEmail = booking?.email;

  // 4️ Find doctor (HOOK 3)
  const doctor = useMemo(() => {
    return REVEALED_DOCTORS.find((d) => d.id === doctorId);
  }, [doctorId]);

  // 5️ Calendar times (HOOK 4, 5)
  const startISO = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return d.toISOString();
  }, []);

  const endISO = useMemo(() => {
    const d = new Date(startISO);
    d.setMinutes(d.getMinutes() + 30);
    return d.toISOString();
  }, [startISO]);

  // 6️ AFTER ALL HOOKS → conditional return (SAFE)
  if (!booking || !doctor) {
    return (
      <div className="p-10 text-center text-muted">
        Invalid or expired booking.
      </div>
    );
  }

  // ⬇
  // BELOW THIS LINE, KEEP YOUR EXISTING JSX RETURN AS-IS
  return (
    <div className="w-full">
      {/* Hero */}
      <div className="rounded-3xl border border-border bg-surface shadow-soft overflow-hidden">
        {/* Fancy gradient header */}
        <div
          className="px-8 py-8"
          style={{
            background:
              "linear-gradient(135deg, rgba(var(--c-primary),0.10), rgba(var(--c-accent),0.10), rgba(255,255,255,0.55))",
          }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                <Icon name="check" className="h-7 w-7" />
              </div>

              <div>
                <div className="text-xs text-muted">Payment successful</div>
                <h1 className="mt-1 text-3xl font-semibold">Congratulations 🎉</h1>
                <p className="mt-2 text-sm text-muted max-w-2xl">
                  Your appointment is confirmed. Doctor identity and full clinic details are now revealed.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/patient/dashboard"
                className="rounded-xl border border-border bg-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-50"
              >
                Go to Dashboard
              </Link>

              <Link
                to="/patient/appointments"
                className="btn-primary-gradient inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white"
              >
                Manage Appointment
                <Icon name="arrowRight" className="h-5 w-5" />
              </Link>
            </div>
          </div>

          {/* Booking ID strip */}
          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-white/70 px-5 py-3">
            <div className="text-sm">
              <span className="text-muted">Booking ID:</span>{" "}
              <span className="font-semibold">{bookingId}</span>
            </div>
            <div className="h-4 w-px bg-border hidden sm:block" />
            <div className="text-sm">
              <span className="text-muted">Patient:</span>{" "}
              <span className="font-semibold">{patientEmail}</span>
            </div>
            <div className="ml-auto inline-flex items-center gap-2 text-xs text-muted">
              <span className="text-success">
                <Icon name="shield" className="h-4 w-4" />
              </span>
              Secure confirmation
            </div>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="mt-6 grid grid-cols-12 gap-5">
        {/* Left: appointment + doctor */}
        <div className="col-span-12 lg:col-span-7 space-y-5">
          <Card title="Appointment Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoTile icon="clock" label="Date & Time" value={slot} />
              <InfoTile icon="info" label="Mode" value={mode} />
              <InfoTile icon="pin" label="Clinic Location" value={`${doctor.clinic}`} />
              <InfoTile icon="shield" label="Price Paid" value={`$${doctor.price}`} />
            </div>

            <div className="mt-4 rounded-2xl border border-border bg-white p-4">
              <div className="text-xs text-muted">Quick note</div>
              <div className="mt-1 text-sm text-muted">
                Please arrive 10 minutes early (clinic) or keep your phone available (video).
                Your doctor may request reports during the consultation.
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button
                onClick={() =>
                  downloadICS({
                    title: `OrbisMed Appointment • ${doctor.specialty}`,
                    description: `Appointment with ${doctor.name} (${doctor.specialty}). Booking ID: ${bookingId}`,
                    location: `${doctor.clinic}, ${doctor.address}`,
                    startISO,
                    endISO,
                  })
                }
                className="rounded-xl border border-border bg-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-50 inline-flex items-center gap-2"
              >
                <Icon name="calendar" />
                Add to Calendar
              </button>

              <button
                onClick={() => alert("Invoice download will be enabled after backend integration.")}
                className="rounded-xl border border-border bg-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-50 inline-flex items-center gap-2"
              >
                <Icon name="download" />
                Download Invoice
              </button>
            </div>
          </Card>

          <Card title="Doctor Details (Revealed)">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-52 shrink-0">
                <div className="rounded-2xl overflow-hidden border border-border bg-slate-50">
                  {/* Here you can replace with real doctor photo later */}
                  <img
                    src={"/images/doctor-blur.png"}
                    alt="Doctor"
                    className="h-40 w-full object-cover"
                    style={{ filter: "none" }}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-lg font-semibold truncate">{doctor.name}</div>
                <div className="mt-1 text-sm text-muted">
                  {doctor.specialty} • ⭐ {doctor.rating.toFixed(1)}
                </div>

                <div className="mt-4 rounded-2xl border border-border bg-white p-4">
                  <div className="text-xs text-muted">Clinic</div>
                  <div className="mt-1 font-semibold text-text">{doctor.clinic}</div>
                  <div className="mt-2 text-sm text-muted leading-relaxed">{doctor.address}</div>
                  <div className="mt-2 text-xs text-muted">{doctor.mapHint}</div>
                </div>

                <div className="mt-4 text-xs text-muted">
                  For safety, OrbisMed reveals doctor identity only after payment confirmation.
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right: terms + policies */}
        <div className="col-span-12 lg:col-span-5 space-y-5">
          <Card title="Cancellation & Refund Policy">
            <div className="space-y-3 text-sm text-muted leading-relaxed">
              <div className="rounded-2xl border border-border bg-white p-4">
                <div className="font-semibold text-text">Policy summary</div>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  <li>
                    <span className="font-semibold text-text">No-show charge:</span> 10% of the consultation fee.
                  </li>
                  <li>
                    <span className="font-semibold text-text">Cancellation charge:</span> 5% of the consultation fee.
                  </li>
                  <li>Reschedule allowed up to <span className="font-semibold text-text">2 hours</span> before the slot (subject to availability).</li>
                </ul>
              </div>

              <div className="rounded-2xl border border-border bg-white p-4">
                <div className="font-semibold text-text">Need to reschedule?</div>
                <div className="mt-2">
                  Go to <span className="font-semibold text-text">Manage Appointment</span> to reschedule/cancel.
                  Refunds (if applicable) are processed back to the original payment method.
                </div>
              </div>
            </div>
          </Card>

          <Card title="What happens next">
            <div className="space-y-3">
              <Step
                title="You’ll receive confirmation"
                desc="We’ll email your booking details and invoice."
              />
              <Step
                title="Doctor will be notified"
                desc="The clinic gets your booking and slot is locked."
              />
              <Step
                title="Join at your time"
                desc={mode === "Video Consultation" ? "We’ll share a secure video link before your slot." : "Visit the clinic address above and arrive 10 minutes early."}
              />
            </div>

            <div className="mt-4 rounded-2xl border border-border bg-white p-4 text-xs text-muted">
              Tip: Keep any reports/photos ready to share during the consultation.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* -----------------------------
  UI bits
----------------------------- */
function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft overflow-hidden">
      <div className="border-b border-border px-5 py-4 font-semibold">{title}</div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function InfoTile({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4">
      <div className="flex items-center gap-2 text-xs text-muted">
        <span className="text-primary">
          <Icon name={icon} className="h-4 w-4" />
        </span>
        {label}
      </div>
      <div className="mt-2 text-sm font-semibold text-text">{value}</div>
    </div>
  );
}

function Step({ title, desc }) {
  return (
    <div className="flex gap-3 rounded-2xl border border-border bg-white p-4">
      <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
        ✓
      </div>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="mt-1 text-sm text-muted">{desc}</div>
      </div>
    </div>
  );
}
